// src/CVEditor.js
import React, { useEffect, useState } from "react";
import { SECTION_TITLES, tUI } from "./i18n";

const LANG_OPTIONS = [
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
];

function sectionLabel(lang, key) {
  const table = SECTION_TITLES[lang] || SECTION_TITLES.en;
  return table?.[key] || key;
}

const emptyExperience = () => ({
  role: "",
  company: "",
  location: "",
  dates: "",
  description: "",
});

const emptyEducation = () => ({
  degree: "",
  institution: "",
  location: "",
  dates: "",
});

function normalizeData(data = {}) {
  return {
    name: data.name || "",
    title: data.title || "",
    email: data.email || "",
    phone: data.phone || "",
    linkedin: data.linkedin || "",
    location: data.location || "",
    nationality: data.nationality || "",
    summary: data.summary || "",
    skills: Array.isArray(data.skills) ? data.skills : [],
    areasOfExpertise: Array.isArray(data.areasOfExpertise)
      ? data.areasOfExpertise
      : [],
    languages: Array.isArray(data.languages) ? data.languages : [],
    experiences:
      Array.isArray(data.experiences) && data.experiences.length
        ? data.experiences
        : [],
    education:
      Array.isArray(data.education) && data.education.length
        ? data.education
        : [],
  };
}

export default function CVEditor({
  data,
  onChange,
  language,
  onLanguageChange,
}) {
  const [form, setForm] = useState(() => normalizeData(data));
  const lang = language || "en";

  // keep local form in sync with incoming data
   // keep local form in sync with incoming data,
  // but only when the *content* actually changes
  useEffect(() => {
    const normalized = normalizeData(data);
    setForm(normalized);
  }, [JSON.stringify(data || {})]);


  // push changes up to App (editedData)
  useEffect(() => {
    if (onChange) onChange(form);
  }, [form, onChange]);

  const field = (name) => (e) =>
    setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const listChange = (fieldName, index, value) => {
    setForm((prev) => {
      const arr = [...(prev[fieldName] || [])];
      arr[index] = value;
      return { ...prev, [fieldName]: arr };
    });
  };

  const listAdd = (fieldName) => {
    setForm((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ""],
    }));
  };

  const listRemove = (fieldName, index) => {
    setForm((prev) => {
      const arr = [...(prev[fieldName] || [])];
      arr.splice(index, 1);
      return { ...prev, [fieldName]: arr };
    });
  };

  const expChange = (index, key, value) => {
    setForm((prev) => {
      const arr = [...(prev.experiences || [])];
      const base = arr[index] || emptyExperience();
      arr[index] = { ...base, [key]: value };
      return { ...prev, experiences: arr };
    });
  };

  const addExperience = () => {
    setForm((prev) => ({
      ...prev,
      experiences: [...(prev.experiences || []), emptyExperience()],
    }));
  };

  const removeExperience = (index) => {
    setForm((prev) => {
      const arr = [...(prev.experiences || [])];
      arr.splice(index, 1);
      return { ...prev, experiences: arr };
    });
  };

  const eduChange = (index, key, value) => {
    setForm((prev) => {
      const arr = [...(prev.education || [])];
      const base = arr[index] || emptyEducation();
      arr[index] = { ...base, [key]: value };
      return { ...prev, education: arr };
    });
  };

  const addEducation = () => {
    setForm((prev) => ({
      ...prev,
      education: [...(prev.education || []), emptyEducation()],
    }));
  };

  const removeEducation = (index) => {
    setForm((prev) => {
      const arr = [...(prev.education || [])];
      arr.splice(index, 1);
      return { ...prev, education: arr };
    });
  };

  return (
    <div className="cv-builder-card">
      {/* Header: title + CV language selector */}
      <div className="cv-builder-header">
        <div className="cv-builder-field cv-builder-lang">
          <label>{tUI(lang, 'label_cv_lang')}</label>
          <select
            value={lang}
            onChange={(e) =>
              onLanguageChange && onLanguageChange(e.target.value)
            }
          >
            {LANG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

            {/* CONTACT DETAILS */}
      <div className="cv-builder-section">
        <h3>{tUI(lang, 'builder_contact_title')}</h3>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_name')}</label>
          <input value={form.name} onChange={field("name")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_headline')}</label>
          <input value={form.title} onChange={field("title")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_email')}</label>
          <input value={form.email} onChange={field("email")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_phone')}</label>
          <input value={form.phone} onChange={field("phone")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_linkedin')}</label>
          <input value={form.linkedin} onChange={field("linkedin")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_location')}</label>
          <input value={form.location} onChange={field("location")} />
        </div>

        <div className="cv-builder-field">
          <label>{tUI(lang, 'builder_label_nationality')}</label>
          <input value={form.nationality} onChange={field("nationality")} />
        </div>
      </div>


      {/* SUMMARY */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "professionalSummary")}</h3>
        <div className="cv-builder-field">
          
          <textarea
            rows={5}
            value={form.summary}
            onChange={field("summary")}
          />
        </div>
      </div>

      {/* AREAS OF EXPERTISE */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "areasOfExpertise")}</h3>

        <div className="cv-builder-tags">
          {(form.areasOfExpertise || []).map((area, idx) => (
            <div key={idx} className="cv-builder-tag-row">
              <input
                value={area}
                onChange={(e) =>
                  listChange("areasOfExpertise", idx, e.target.value)
                }
                placeholder="e.g. Data Science, Marketing Analytics"
              />
              <button
                type="button"
                className="cv-builder-tag-remove"
                onClick={() => listRemove("areasOfExpertise", idx)}
                aria-label="Remove"
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            className="cv-builder-add-btn"
            onClick={() => listAdd("areasOfExpertise")}
          >
            {tUI(lang, 'builder_add_area')}
          </button>
        </div>
      </div>

      {/* SKILLS */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "skills")}</h3>

        <div className="cv-builder-tags">
          {(form.skills || []).map((skill, idx) => (
            <div key={idx} className="cv-builder-tag-row">
              <input
                value={skill}
                onChange={(e) => listChange("skills", idx, e.target.value)}
                placeholder="e.g. Python, SQL, Stakeholder management"
              />
              <button
                type="button"
                className="cv-builder-tag-remove"
                onClick={() => listRemove("skills", idx)}
                aria-label="Remove"
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            className="cv-builder-add-btn"
            onClick={() => listAdd("skills")}
          >
            {tUI(lang, 'builder_add_skill')}
          </button>
        </div>
      </div>

      {/* LANGUAGES */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "languages")}</h3>

        <div className="cv-builder-tags">
          {(form.languages || []).map((lng, idx) => (
            <div key={idx} className="cv-builder-tag-row">
              <input
                value={lng}
                onChange={(e) => listChange("languages", idx, e.target.value)}
                placeholder="e.g. English (C1), German (B2)"
              />
              <button
                type="button"
                className="cv-builder-tag-remove"
                onClick={() => listRemove("languages", idx)}
                aria-label="Remove"
              >
                ×
              </button>
            </div>
          ))}

          <button
            type="button"
            className="cv-builder-add-btn"
            onClick={() => listAdd("languages")}
          >
            {tUI(lang, 'builder_add_language')}
          </button>
        </div>
      </div>

      {/* EXPERIENCE */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "experience")}</h3>

        {(form.experiences || []).map((exp, idx) => (
          <div key={idx} className="cv-builder-experience">
            <div className="cv-builder-two-cols">
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_exp_role')}</label>
                <input
                  value={exp.role || ""}
                  onChange={(e) => expChange(idx, "role", e.target.value)}
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_exp_company')}</label>
                <input
                  value={exp.company || ""}
                  onChange={(e) => expChange(idx, "company", e.target.value)}
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_exp_location')}</label>
                <input
                  value={exp.location || ""}
                  onChange={(e) => expChange(idx, "location", e.target.value)}
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_exp_dates')}</label>
                <input
                  value={exp.dates || ""}
                  onChange={(e) => expChange(idx, "dates", e.target.value)}
                  placeholder={tUI(lang, 'builder_exp_dates_ph')}
                />
              </div>
            </div>

            <div className="cv-builder-field">
              <label>{tUI(lang, 'builder_exp_desc')}</label>
              <textarea
                rows={3}
                value={exp.description || ""}
                onChange={(e) =>
                  expChange(idx, "description", e.target.value)
                }
              />
            </div>


            <button
              type="button"
              className="cv-builder-add-btn"
              onClick={() => removeExperience(idx)}
            >
              {tUI(lang, 'builder_remove_experience')}
            </button>
          </div>
        ))}

        <button
          type="button"
          className="cv-builder-add-btn"
          onClick={addExperience}
        >
          {tUI(lang, 'builder_add_experience')}
        </button>
      </div>

      {/* EDUCATION */}
      <div className="cv-builder-section">
        <h3>{sectionLabel(lang, "education")}</h3>

        {(form.education || []).map((ed, idx) => (
          <div key={idx} className="cv-builder-experience">
            <div className="cv-builder-two-cols">
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_edu_degree')}</label>
                <input
                  value={ed.degree || ""}
                  onChange={(e) => eduChange(idx, "degree", e.target.value)}
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_edu_institution')}</label>
                <input
                  value={ed.institution || ""}
                  onChange={(e) =>
                    eduChange(idx, "institution", e.target.value)
                  }
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_edu_location')}</label>
                <input
                  value={ed.location || ""}
                  onChange={(e) =>
                    eduChange(idx, "location", e.target.value)
                  }
                />
              </div>
              <div className="cv-builder-field">
                <label>{tUI(lang, 'builder_edu_dates')}</label>
                <input
                  value={ed.dates || ""}
                  onChange={(e) => eduChange(idx, "dates", e.target.value)}
                  placeholder={tUI(lang, 'builder_edu_dates_ph')}
                />
              </div>
            </div>


            <button
              type="button"
              className="cv-builder-add-btn"
              onClick={() => removeEducation(idx)}
            >
              {tUI(lang, 'builder_remove_education')}
            </button>
          </div>
        ))}

        <button
          type="button"
          className="cv-builder-add-btn"
          onClick={addEducation}
        >
          {tUI(lang, 'builder_add_education')}
        </button>
      </div>
    </div>
  );
}
